import type { ConversationDotStatus } from "@app/lib/utils/conversation_dot_status";
import type { ModelId } from "@app/types/shared/model_id";

/**
 * What the person has to supply before the work can move. Kept apart from the
 * task's lifecycle status: a `todo` can need a decision, an `in_review` a click.
 */
export const TASK_USER_NEEDS = [
  "none",
  "start",
  "review",
  "decision",
  "input",
  "approval",
] as const;

export type TaskUserNeed = (typeof TASK_USER_NEEDS)[number];

export const POD_TASK_STATUSES = [
  "todo",
  "in_progress",
  "in_review",
  "done",
] as const;

export type PodTaskStatus = (typeof POD_TASK_STATUSES)[number];

export const POD_TASK_ACTOR_TYPES = ["user", "agent"] as const;

export type PodTaskActorType = (typeof POD_TASK_ACTOR_TYPES)[number];

export const POD_TASK_SOURCE_TYPES = [
  "conversation",
  "project_conversation",
  "project_knowledge",
  "slack",
  "notion",
  "gdrive",
  "confluence",
  "github",
  "microsoft",
  "gmail",
  "granola",
  "hubspot",
  "calendar",
  "wiki_person",
  "wiki_project",
  "routine_template",
] as const;

export type PodTaskSourceType = (typeof POD_TASK_SOURCE_TYPES)[number];

export const AGENT_SUGGESTION_STATUSES = [
  "pending",
  "approved",
  "rejected",
] as const;
export type AgentSuggestionStatus = (typeof AGENT_SUGGESTION_STATUSES)[number];

export const POD_TASK_PERIOD_SCOPES = [
  "active",
  "last_24h",
  "last_7d",
  "last_30d",
] as const;
export type PodTaskPeriodScope = (typeof POD_TASK_PERIOD_SCOPES)[number];

export function isPodTaskPeriodScope(v: string): v is PodTaskPeriodScope {
  return POD_TASK_PERIOD_SCOPES.includes(v as PodTaskPeriodScope);
}

export const POD_TASK_PEOPLE_SCOPES = ["just_mine", "all_project"] as const;
export type PodTaskPeopleScope = (typeof POD_TASK_PEOPLE_SCOPES)[number];

export function isPodTaskPeopleScope(v: string): v is PodTaskPeopleScope {
  return POD_TASK_PEOPLE_SCOPES.includes(v as PodTaskPeopleScope);
}

export type PodTaskSourceInfo = {
  sourceType: PodTaskSourceType;
  sourceId: string;
  sourceTitle: string | null;
  sourceUrl: string | null;
};

/**
 * Who a proposed owner turned out to be. Only `member` and `workspace_member`
 * are people this workspace can hand work to; the others are kept by name so
 * the task never pretends to have an owner it does not.
 */
export const TASK_OWNER_RESOLUTIONS = [
  "member",
  "workspace_member",
  "other_organization",
  "external",
  "unclear",
] as const;
export type TaskOwnerResolution = (typeof TASK_OWNER_RESOLUTIONS)[number];

export function isTaskOwnerResolution(v: string): v is TaskOwnerResolution {
  return TASK_OWNER_RESOLUTIONS.includes(v as TaskOwnerResolution);
}

export const TASK_CONFIDENCES = ["high", "medium", "low"] as const;
export type TaskConfidence = (typeof TASK_CONFIDENCES)[number];

export const TASK_EVIDENCE_SOURCE_KINDS = [
  "meeting",
  "file",
  "conversation",
  "email",
  "slack",
] as const;
export type TaskEvidenceSourceKind =
  (typeof TASK_EVIDENCE_SOURCE_KINDS)[number];

export const TASK_EVIDENCE_LOCATOR_KINDS = [
  "timestamp",
  "message",
  "url",
] as const;
export type TaskEvidenceLocatorKind =
  (typeof TASK_EVIDENCE_LOCATOR_KINDS)[number];

export type PodTaskProposedOwnerInfo = {
  name: string | null;
  email: string | null;
  user: PodTaskAssigneeType | null;
  resolution: TaskOwnerResolution;
};

/** One source link with the verbatim span it contributed and where it sits. */
export type PodTaskEvidenceInfo = PodTaskSourceInfo & {
  excerpt: string | null;
  excerptVerified: boolean | null;
  locator: {
    kind: TaskEvidenceLocatorKind;
    value: string;
    url: string | null;
  } | null;
};

export type PodTaskAssigneeType = {
  sId: string;
  fullName: string;
  image: string | null;
};

/** Stable row / stable-order key when grouping tasks with no assignee. */
export const POD_TASK_UNASSIGNED_GROUP_KEY = "__unassigned__";

/** Header and menu copy for tasks with no assignee. */
export const POD_TASK_NO_ASSIGNEE_LABEL = "No assignee";

/** Seeded via POST /pods/:podId/tasks/seed (editors only). */
export const POD_MANAGER_AGENT_SID = "pod_manager" as const;

export type PodTaskType = {
  id: ModelId;
  sId: string;
  user: PodTaskAssigneeType | null;
  conversationId: string | null;
  /** Same semantics as the left sidebar conversation row (see `getConversationDotStatus`). */
  conversationSidebarStatus: ConversationDotStatus | null;
  conversationIsRunningAgentLoop: boolean | null;
  text: string;
  status: PodTaskStatus;
  /**
   * Manual rank inside the task's container and workflow column. Contiguous
   * non-negative integers, rewritten as a block whenever a column is reordered.
   */
  position: number;
  doneAt: Date | null;
  /** Optional persisted instructions merged into the kickoff prompt when the todo is started. */
  agentInstructions: string | null;
  actorRationale: string | null;
  createdByType: PodTaskActorType;
  createdByAgentConfigurationId: string | null;
  createdByUserId: string | null;
  agentSuggestionStatus: AgentSuggestionStatus | null;
  agentSuggestionReviewedAt: Date | null;
  markedAsDoneByType: PodTaskActorType | null;
  markedAsDoneByAgentConfigurationId: string | null;
  markedAsDoneByUserId: string | null;
  sources: PodTaskSourceInfo[];
  snoozedUntil: Date | null;
  pinnedAt: Date | null;
  /**
   * The day the task is due, `YYYY-MM-DD`. A deadline is a day, not an instant:
   * stored as a plain date so it reads the same in every timezone and needs no
   * conversion between the picker, the wire and the calendar.
   */
  dueOn: string | null;
  /** Null on rows written before suggestions carried it. */
  userNeed: TaskUserNeed | null;
  ctaLabel: string | null;
  /** sId of the human an agent proposed this for; null on human-made rows. */
  requestedByUserId: string | null;
  proposedOwner: PodTaskProposedOwnerInfo | null;
  confidence: TaskConfidence | null;
  /** sId of the meeting the task was drawn from; absent where meetings are not read. */
  meetingId?: string | null;
  note: string | null;
  createdAt: Date;
  updatedAt: Date;
};

/** A file an agent produced while working a todo, offered as "Review" on Home. */
export type PodTaskArtifactInfo = {
  fileId: string;
  title: string;
  contentType: string;
  /** Conversation the artifact was produced in — the artifact's origin link. */
  conversationId: string;
};

/**
 * A task another human's chief of staff proposed into this Home. The requester
 * and the COS that spoke for them are read back from the `handoff.requested`
 * envelope, which is where the lineage lives — the task row carries only what a
 * task carries.
 */
export type PersonalTaskHandoffInfo = {
  requestedByUserId: string;
  requestedByName: string;
  requestedByAgentId: string;
};

/** Where a task lives, when it is not personal: the breadcrumb a row wears. */
export type PersonalTaskProjectInfo = { sId: string; name: string };

/**
 * Who is actually working the task right now — Friday, the delegate Friday
 * handed it to, or the human who took it over. Derived from the newest
 * run-ownership envelope rather than stored, so it cannot drift from the trail.
 */
export type PersonalTaskExecutorInfo = {
  kind: "agent" | "human";
  /** `sId` of the agent configuration, or of the user. */
  id: string;
  name: string;
};

/**
 * Whose list this row is being read from. `owner` is the task's own Home;
 * `requester` is a colleague looking at work they handed over, which is a
 * read-only view — every write path is scoped to the owner regardless.
 */
export type PersonalTaskViewerRole = "owner" | "requester";

/**
 * A colleague this task's run is parked on: the gate stopped an action and
 * named them the approver, so the owner can only wait or nudge. Null whenever
 * the pause is the owner's own to decide.
 */
export type PersonalTaskWaitingOnInfo = {
  userId: string;
  name: string;
  since: string;
};

export type PersonalTaskType = PodTaskType & {
  artifacts: PodTaskArtifactInfo[];
  /** Colleagues mentioned in this task's thread, oldest first. */
  contributors: PodTaskAssigneeType[];
  /** Who is doing the work now, read off the newest run-ownership event. */
  executor: PersonalTaskExecutorInfo | null;
  handoff: PersonalTaskHandoffInfo | null;
  /** Set when the newest run event on the task is a failure. */
  lastRunFailedAt: Date | null;
  project: PersonalTaskProjectInfo | null;
  viewerRole: PersonalTaskViewerRole;
  waitingOn: PersonalTaskWaitingOnInfo | null;
};

// `PodTaskType` declares several `Date` fields. On the wire they JSON-serialize
// to ISO strings, so every response body and every client that reads one uses
// the wire shape instead.
export type PodTaskWireType = Omit<
  PodTaskType,
  | "doneAt"
  | "agentSuggestionReviewedAt"
  | "snoozedUntil"
  | "pinnedAt"
  | "createdAt"
  | "updatedAt"
> & {
  doneAt: string | null;
  agentSuggestionReviewedAt: string | null;
  snoozedUntil: string | null;
  pinnedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type PersonalTaskWireType = PodTaskWireType & {
  artifacts: PodTaskArtifactInfo[];
  contributors: PodTaskAssigneeType[];
  executor: PersonalTaskExecutorInfo | null;
  handoff: PersonalTaskHandoffInfo | null;
  lastRunFailedAt: string | null;
  project: PersonalTaskProjectInfo | null;
  viewerRole: PersonalTaskViewerRole;
  waitingOn: PersonalTaskWaitingOnInfo | null;
};

export const POD_TASK_COMMIT_BASES = [
  "question_answer",
  "direct_request",
  "ui",
] as const;
export type PodTaskCommitBasis = (typeof POD_TASK_COMMIT_BASES)[number];

export type PodTaskActivityKind =
  | "created"
  | "text"
  | "status"
  | "assignee"
  | "due"
  | "proposed_owner"
  | "accepted"
  | "dismissed"
  | "assignment_declined"
  | "handoff_requested"
  | "handoff_accepted"
  | "handoff_declined";

/** One line of a task's history, read from its versions and its envelopes. */
export type PodTaskActivityEntry = {
  at: string;
  kind: PodTaskActivityKind;
  actorName: string | null;
  from: string | null;
  to: string | null;
};

export type PodTaskDetailWireType = PodTaskWireType & {
  evidence: PodTaskEvidenceInfo[];
  createdByAgent: { sId: string; name: string } | null;
  createdByUser: PodTaskAssigneeType | null;
  requestedBy: PodTaskAssigneeType | null;
  acceptedBy: PodTaskAssigneeType | null;
  acceptedAt: string | null;
  commitBasis: PodTaskCommitBasis | null;
  activity: PodTaskActivityEntry[];
};
